import React, { useState } from 'react';
import './QuestionnaireStagesHost.scss';
import {
    DataRequestProject,
    DataRequestProjectState,
} from '../../../models/DataRequestHub/DataRequestProject';
import QuestionnaireStagesList from '../QuestionnaireStagesList/QuestionnaireStagesList';
import { QuestionnaireModuleHost } from '../DataRequestDashboardPage/QuestionnaireModuleHost/QuestionnaireModuleHost';
import { useNavigate } from 'react-router-dom';
import { DesignModeSwitcher } from '../DesignModeSwitcher/DesignModeSwitcher';
import QuestionSeenStatus from '../../../models/DataRequestHub/QuestionSeenStatus';
import { EditorModeEnum } from '../../../models/DataRequestHub/ProjectEditorModeEnum';
import { EditorTypeEnum } from '../../../models/DataRequestHub/ProjectEditorEnum';
import moment from 'moment';
import createNotification from '../../../utils/createNotification';
import { useStateSelector } from '../../../store/selectors';
import ButtonLoader from '../../../components/Layout/Buttons/ButtonLoader';
import { useDataRequestUrl } from '../../../hooks/useDataRequestDashboardUrl';
import { PopupsWizard } from '../../../components/PopupsWizard/PopupsWizard';
import { SnapshotsPopup } from '../SnapshotsPopup/SnapshotsPopup';
import { DataForm } from '../../../models/DataRequestHub/DataForm';
import { QuestionnaireFormPopup } from '../QuestionnaireFormPopup/QuestionnaireFormPopup';
import { useIsDataRequestProjectReadonly } from '../../../custom-hooks/useIsDataRequestProjectReadonly';

interface QuestionnaireStagesHostProps {
    projects: DataRequestProject[];
    isAdminView: boolean;
    isReadOnly?: boolean;
    updateProjectsData(isInitialFetch?: boolean): void;
    preselectedQuestionnaireFormId: number;
    questionsSeenStatus: QuestionSeenStatus[];
    markQuestionsAsSeen(
        formId: number,
        questionsIds: number[],
        subQuestionsIds: number[]
    ): void;
    isBuiltIn: boolean;
    isSingleitemProject?: boolean;
    engagementId?: number;
}

const QuestionnaireStagesHost = (props: QuestionnaireStagesHostProps) => {
    const [selectedQuestionnaireId, setSelectedQuestionnaireId] =
        useState<number>(props.preselectedQuestionnaireFormId);
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const axios = useStateSelector((state) => state.core.axios);
    const editorUrl = useDataRequestUrl(true);
    const [isSnapshotsPopupVisible, setIsSnapshotsPopupVisible] =
        useState(false);
    const questionsSeenStatus = useStateSelector(
        (state) => state.dataRequestsNotifications.questionsSeenStatus
    );
    const [selectedSnaphotForm, setSelectedSnaphotForm] = useState<DataForm>();
    const [selectedProject, setSelectedProject] =
        useState<DataRequestProject | null>(null);
    const isReadonlyProject = useIsDataRequestProjectReadonly(props.projects, props.engagementId) || !!props.isReadOnly;

    const handleStageEditIconClicked = (formId: number) => {
        const targetProject =
            props.projects.find((project) =>
                project.dataForms.some((form) => form.id === formId)
            ) ?? props.projects.find((project) => project);

        if (targetProject) {
            navigate(`/${editorUrl}`, {
                state: {
                    questionnaireEditorParams: {
                        targetQuestionnaireId: formId,
                    },
                    project: targetProject,
                    shouldNavigateToProjectViewMode: true,
                    type: EditorTypeEnum.Questionnaire,
                    mode: EditorModeEnum.EditProject,
                    additionalUrlToReturn: `?tab=${
                        EditorTypeEnum.Questionnaire + 1
                    }`,
                    isSingleitemProject: props.isSingleitemProject,
                    isExternalProject: targetProject.isExternalProject,
                },
            });
        }
    };

    const renderStagesList = () => (
        <QuestionnaireStagesList
            onStageNameClick={setSelectedQuestionnaireId}
            onEditIconClick={handleStageEditIconClicked}
            isEditorMode={false}
            invalidStageIds={[]}
            isSingleitemProject={props.isSingleitemProject}
            {...props}
        />
    );

    const isProjectActive = (formId: number) => {
        const targetProject = getProjectByFormId(formId);

        return (
            targetProject.state === DataRequestProjectState.Active &&
            !isReadonlyProject
        );
    };

    const getProject = (projectId: number) => {
        return props.projects.find((x) => x.id === projectId);
    };

    const getProjectByFormId = (formId: number) => {
        return (
            props.projects.find((project) =>
                project.dataForms.some((form) => form.id === formId)
            ) ?? props.projects.find((project) => project)
        );
    };

    const renderSnapshotPopup = () => {
        const targetProject = getProjectByFormId(selectedQuestionnaireId);
        const questionnaireForm = getQuestionnaireForm();

        let popupHeader =
            questionnaireForm.customName ??
            questionnaireForm.originalTemplateName + ' - Data Snapshots';
        let snapshotFileName = `${targetProject.practiceName} - ${
            questionnaireForm.customName ??
            questionnaireForm.originalTemplateName
        }`;

        return (
            <>
                <PopupsWizard
                    popups={[
                        <SnapshotsPopup
                            projectId={targetProject.id}
                            objectId={questionnaireForm.id}
                            header={popupHeader}
                            setSelectedForm={(form: DataForm) => {
                                setSelectedSnaphotForm(form);
                                const targetProject = getProjectByFormId(
                                    form?.id
                                );
                                setSelectedProject(targetProject);
                            }}
                            onClose={() => setIsSnapshotsPopupVisible(false)}
                            isArchiveMode={false}
                            snapshotFileName={snapshotFileName}
                            isAdminView={props.isAdminView}
                            isReadonlyMode={isReadonlyProject || !!props.isReadOnly}
                            mode="form"
                        />,

                        <QuestionnaireFormPopup
                            header={
                                selectedSnaphotForm?.customName ??
                                selectedSnaphotForm?.originalTemplateName
                            }
                            isAdminView={props.isAdminView}
                            form={selectedSnaphotForm}
                            project={selectedProject}
                            isReadonlyMode={true}
                            onlyShowQuestionColumns={!props.isAdminView}
                            updateProjectsData={() => {}}
                            questionsSeenStatus={questionsSeenStatus}
                        />,
                    ]}
                />
            </>
        );
    };

    const getQuestionnaireSnapshotFile = (questionnaireFormId: number) => {
        setIsLoading(true);
        const targetProject =
            props.projects.find((project) =>
                project.dataForms.some(
                    (form) => form.id === questionnaireFormId
                )
            ) ?? props.projects.find((project) => project);

        const requestParamsObj = {
            params: {
                snapshotFormId: questionnaireFormId,
                includeAllTables: false,
            },
            responseType: 'blob' as const,
        };
        axios
            .get(
                `api/dataRequestProjects/${targetProject.id}/dataForms/${questionnaireFormId}/dataFormSnapshots/file`,
                requestParamsObj
            )
            .then((response) => {
                if (response.status !== 200) {
                    createNotification(
                        'An error occured while downloading file',
                        'error'
                    );
                } else {
                    const url = window.URL.createObjectURL(
                        new Blob([response.data])
                    );

                    const snapshoDateStringFormat = moment(new Date()).format(
                        'MM-DD-YYYY'
                    );
                    const targetForm = targetProject.dataForms.find(
                        (x) => x.id === questionnaireFormId
                    );
                    const fileName = `${targetProject.practiceName} - ${targetForm.customName} ${snapshoDateStringFormat}.xlsx`;
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', fileName);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }
            })
            .finally(() => setIsLoading(false));
    };

    const renderStageView = () => {
        const projectId = props.projects.find((f) =>
            f.dataForms.some((s) => s.id === selectedQuestionnaireId)
        ).id;
        const questionnaireForm = getQuestionnaireForm();
        const projectVariables = props.projects.find((f) =>
            f.dataForms.some((s) => s.id === selectedQuestionnaireId)
        ).variables;
        return (
            <>
                <div className="questionnaire-stages-host-header">
                    <div className="stage-navigation-breadcrumb">
                        <label
                            className="link-text"
                            onClick={() => setSelectedQuestionnaireId(null)}>
                            Questionnaire
                        </label>
                        <label className="node-separator">{' / '}</label>
                        <label className="link-text">
                            {questionnaireForm.customName}
                        </label>
                    </div>
                    {props.isAdminView && (
                        <div className="right-header">
                            <div
                                className="link-text"
                                onClick={() =>
                                    setIsSnapshotsPopupVisible(true)
                                }>
                                Data Snapshots
                            </div>
                            <ButtonLoader
                                type="submit"
                                className={'btn btn-primary'}
                                disabled={isLoading}
                                isLoading={isLoading}
                                loaderButtonText={''}
                                buttonText={'Export to Excel'}
                                onClick={() => {
                                    getQuestionnaireSnapshotFile(
                                        questionnaireForm.id
                                    );
                                }}
                            />
                            {isProjectActive(questionnaireForm.id) && (
                                <DesignModeSwitcher
                                    defaultValue={false}
                                    onEnable={handleStageEditIconClicked}
                                    formId={questionnaireForm.id}
                                />
                            )}
                        </div>
                    )}
                </div>

                <QuestionnaireModuleHost
                    key={'questionnaire-view-form'}
                    isAdminView={props.isAdminView}
                    isReadonlyMode={isReadonlyProject || !!props.isReadOnly}
                    projectId={projectId}
                    questionnaireForm={questionnaireForm}
                    updateProjectsData={props.updateProjectsData}
                    projectVariables={projectVariables}
                    markQuestionsAsSeen={props.markQuestionsAsSeen}
                    questionsSeenStatus={props.questionsSeenStatus}
                    project={getProject(projectId)}
                    isBuiltIn={props.isBuiltIn}
                />
            </>
        );
    };

    const getQuestionnaireForm = () =>
        props.projects
            .flatMap((project) => project.dataForms)
            .find((form) => form.id === selectedQuestionnaireId);

    return (
        <div className="questionnaire-stages-host">
            {selectedQuestionnaireId ? renderStageView() : renderStagesList()}
            {selectedQuestionnaireId && isSnapshotsPopupVisible
                ? renderSnapshotPopup()
                : []}
        </div>
    );
};

export default QuestionnaireStagesHost;
